<script>
import appConfig from '@src/app.config'
import Layout from '@layouts/main'
import PageHeader from '@components/page-header'
import axios from 'axios'
import VueApexCharts from 'vue-apexcharts'
import jsPDF from 'jspdf'

export default {
  page: {
    title: 'Prisma Cloud Assets',
    meta: [{ name: 'description', content: appConfig.description }]
  },
  components: {
    Layout,
    PageHeader,
    apexchart: VueApexCharts
  },
  data() {
    return {
      title: 'Prisma Cloud Assets Dashboard',
      loading: true,
      updated: '',

      // Stats data
      stats: {
        total_assets: 0,
        critical_assets: 0,
        high_risk_assets: 0,
        internet_exposed_assets: 0,
        host_count: 0,
        package_count: 0,
        total_vulnerabilities: 0,
        avg_vulns_per_asset: 0
      },

      // Asset data
      assets: [],
      filteredAssets: [],

      // Filter options
      selectedSeverities: ['critical', 'high'],
      severityOptions: [
        { value: 'critical', text: 'Critical' },
        { value: 'high', text: 'High' },
        { value: 'medium', text: 'Medium' },
        { value: 'low', text: 'Low' }
      ],

      selectedAssetType: 'all',
      assetTypeOptions: [
        { value: 'all', text: 'All Types' },
        { value: 'host', text: 'Host' },
        { value: 'package', text: 'Package' }
      ],

      selectedServiceProvider: 'all',
      serviceProviders: [],

      selectedInternetExposed: 'all',
      internetExposedOptions: [
        { value: 'all', text: 'All' },
        { value: 'Y', text: 'Internet Exposed' },
        { value: 'N', text: 'Not Exposed' }
      ],

      selectedRiskLevel: 'all',
      riskLevelOptions: [
        { value: 'all', text: 'All Risk Levels' },
        { value: 'critical', text: 'Critical Risk' },
        { value: 'high', text: 'High Risk' },
        { value: 'medium', text: 'Medium Risk' },
        { value: 'low', text: 'Low Risk' }
      ],

      selectedDateRange: '90',
      dateRangeOptions: [
        { value: '7', text: 'Last 7 Days' },
        { value: '30', text: 'Last 30 Days' },
        { value: '60', text: 'Last 60 Days' },
        { value: '90', text: 'Last 90 Days' },
        { value: '180', text: 'Last 180 Days' },
        { value: '365', text: 'Last Year' }
      ],

      searchQuery: '',

      // Chart data
      assetTypeDistribution: [],
      topVulnerableAssets: [],
      riskDistribution: [],
      severityByAssetType: [],
      assetTrends: [],
      providerDistribution: [],

      // Chart options
      assetTypeChartOptions: {
        chart: {
          type: 'donut',
          height: 280
        },
        labels: [],
        colors: ['#5470C6', '#73C0DE', '#9A60B4', '#EA7CCC'],
        legend: {
          position: 'bottom',
          fontSize: '12px'
        },
        plotOptions: {
          pie: {
            donut: {
              size: '60%',
              labels: {
                show: true,
                total: {
                  show: true,
                  label: 'Total Assets',
                  formatter: function (w) {
                    return w.globals.seriesTotals.reduce((a, b) => a + b, 0)
                  }
                }
              }
            }
          }
        }
      },
      assetTypeChartSeries: [],

      topVulnerableAssetsChartOptions: {
        chart: {
          type: 'bar',
          height: 320,
          toolbar: { show: false }
        },
        plotOptions: {
          bar: {
            horizontal: true,
            distributed: true,
            barHeight: '70%'
          }
        },
        dataLabels: {
          enabled: true,
          offsetX: 30,
          style: { fontSize: '12px', colors: ['#fff'] }
        },
        xaxis: {
          categories: [],
          title: { text: 'Vulnerability Count' }
        },
        yaxis: {
          labels: {
            maxWidth: 200
          }
        },
        colors: [],
        legend: { show: false }
      },
      topVulnerableAssetsChartSeries: [],

      riskDistributionChartOptions: {
        chart: {
          type: 'pie',
          height: 280
        },
        labels: [],
        colors: ['#802020', '#DC001E', '#FECA00', '#01AD00'],
        legend: {
          position: 'bottom',
          fontSize: '12px'
        }
      },
      riskDistributionChartSeries: [],

      severityByAssetTypeChartOptions: {
        chart: {
          type: 'bar',
          height: 320,
          stacked: true,
          toolbar: { show: false }
        },
        plotOptions: {
          bar: {
            horizontal: false
          }
        },
        xaxis: {
          categories: []
        },
        yaxis: {
          title: { text: 'Vulnerability Count' }
        },
        legend: {
          position: 'bottom'
        },
        colors: ['#802020', '#DC001E', '#FECA00', '#01AD00']
      },
      severityByAssetTypeChartSeries: [],

      assetTrendsChartOptions: {
        chart: {
          type: 'area',
          height: 320,
          toolbar: { show: false }
        },
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 2 },
        xaxis: {
          categories: [],
          title: { text: 'Month' }
        },
        yaxis: {
          title: { text: 'Asset Count' }
        },
        colors: ['#556ee6', '#802020', '#DC001E'],
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.7,
            opacityTo: 0.3
          }
        }
      },
      assetTrendsChartSeries: [],

      providerDistributionChartOptions: {
        chart: {
          type: 'bar',
          height: 320,
          toolbar: { show: false }
        },
        plotOptions: {
          bar: {
            horizontal: true,
            distributed: false,
            barHeight: '70%'
          }
        },
        dataLabels: {
          enabled: true,
          offsetX: 30,
          style: { fontSize: '12px', colors: ['#fff'] }
        },
        xaxis: {
          categories: [],
          title: { text: 'Asset Count' }
        },
        colors: ['#556ee6'],
        legend: { show: false }
      },
      providerDistributionChartSeries: [],

      // Table configuration
      columns: [
        { key: 'ASSET_NAME', label: 'Asset Name', sortable: true },
        { key: 'ASSET_TYPE', label: 'Type', sortable: true },
        { key: 'SERVICE_PROVIDER', label: 'Provider', sortable: true },
        { key: 'OWNER', label: 'Owner', sortable: true },
        { key: 'VULNERABILITY_COUNT', label: 'Vulnerabilities', sortable: true },
        { key: 'CRITICAL_COUNT', label: 'Critical', sortable: true },
        { key: 'HIGH_COUNT', label: 'High', sortable: true },
        { key: 'EXPLOITABLE_COUNT', label: 'Exploitable', sortable: true },
        { key: 'INTERNET_EXPOSED', label: 'Internet Exposed', sortable: true },
        { key: 'RISK_SCORE', label: 'Risk Score', sortable: true },
        { key: 'LAST_SEEN', label: 'Last Seen', sortable: true }
      ],

      sortKey: 'RISK_SCORE',
      sortDirection: 'desc',

      // Pagination
      currentPage: 1,
      pageSize: 50,
      totalCount: 0,
      totalPages: 0,

      // Export and loading states
      exporting: false,
      tableLoading: false,

      items: [
        { text: 'Home', href: '/' },
        { text: 'Prisma Cloud', href: '/prismacloud' },
        { text: 'Assets', active: true }
      ],

      appConfig: process.env.NODE_ENV === 'production' ? require('@src/app.config') : require('@src/app.config.dev')
    }
  },

  computed: {
    filterParams() {
      const params = {}

      if (this.selectedSeverities.length > 0 && this.selectedSeverities.length < 4) {
        params.severity = this.selectedSeverities.join(',')
      }

      if (this.selectedAssetType !== 'all') {
        params.asset_type = this.selectedAssetType
      }

      if (this.selectedServiceProvider !== 'all') {
        params.service_provider = this.selectedServiceProvider
      }

      if (this.selectedInternetExposed !== 'all') {
        params.internet_exposed = this.selectedInternetExposed
      }

      if (this.selectedRiskLevel !== 'all') {
        params.risk_level = this.selectedRiskLevel
      }

      if (this.selectedDateRange) {
        params.date_range = this.selectedDateRange
      }

      if (this.searchQuery) {
        params.search = this.searchQuery
      }

      return params
    }
  },

  watch: {
    selectedSeverities: { handler() { this.loadAllData() } },
    selectedAssetType: { handler() { this.loadAllData() } },
    selectedServiceProvider: { handler() { this.loadAllData() } },
    selectedInternetExposed: { handler() { this.loadAllData() } },
    selectedRiskLevel: { handler() { this.loadAllData() } },
    selectedDateRange: { handler() { this.loadAllData() } },
    searchQuery: {
      handler() { this.debouncedSearch() },
      immediate: false
    }
  },

  mounted() {
    this.loadServiceProviders()
    this.loadAllData()
  },

  methods: {
    buildQueryString(params) {
      return Object.keys(params)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
        .join('&')
    },

    debouncedSearch: (function() {
      let timeout = null
      return function() {
        clearTimeout(timeout)
        timeout = setTimeout(() => {
          this.loadAllData()
        }, 500)
      }
    })(),

    async loadServiceProviders() {
      const baseURL = this.appConfig.xdrAPI.baseURL || 'http://localhost:5000'
      try {
        const response = await axios.get(`${baseURL}/prismacloud/assets/service-providers`)
        if (response.data && response.data.service_providers) {
          this.serviceProviders = [
            { value: 'all', text: 'All Providers' },
            ...response.data.service_providers.map(sp => ({ value: sp, text: sp }))
          ]
        }
      } catch (error) {
        console.error('Error loading service providers:', error)
      }
    },

    async loadAllData() {
      this.loading = true
      this.tableLoading = true
      const baseURL = this.appConfig.xdrAPI.baseURL || 'http://localhost:5000'
      const queryString = this.buildQueryString(this.filterParams)

      try {
        // Load all data in parallel
        const [
          statsResponse,
          assetTypeResponse,
          topVulnerableResponse,
          riskDistResponse,
          severityByTypeResponse,
          trendsResponse,
          providerDistResponse,
          assetsResponse
        ] = await Promise.all([
          axios.get(`${baseURL}/prismacloud/assets/stats?${queryString}`),
          axios.get(`${baseURL}/prismacloud/assets/asset-type-distribution?${queryString}`),
          axios.get(`${baseURL}/prismacloud/assets/top-vulnerable?${queryString}`),
          axios.get(`${baseURL}/prismacloud/assets/risk-distribution?${queryString}`),
          axios.get(`${baseURL}/prismacloud/assets/severity-by-type?${queryString}`),
          axios.get(`${baseURL}/prismacloud/assets/trends?${queryString}`),
          axios.get(`${baseURL}/prismacloud/assets/provider-distribution?${queryString}`),
          axios.get(`${baseURL}/prismacloud/assets?${queryString}&page=${this.currentPage}&page_size=${this.pageSize}&sort_by=${this.sortKey}&sort_dir=${this.sortDirection}`)
        ])

        // Process stats
        if (statsResponse.data) {
          this.stats = statsResponse.data
        }

        // Process asset type distribution
        if (assetTypeResponse.data && assetTypeResponse.data.distribution) {
          this.renderAssetTypeChart(assetTypeResponse.data.distribution)
        }

        // Process top vulnerable assets
        if (topVulnerableResponse.data && topVulnerableResponse.data.assets) {
          this.renderTopVulnerableAssetsChart(topVulnerableResponse.data.assets)
        }

        // Process risk distribution
        if (riskDistResponse.data && riskDistResponse.data.distribution) {
          this.renderRiskDistributionChart(riskDistResponse.data.distribution)
        }

        // Process severity by asset type
        if (severityByTypeResponse.data && severityByTypeResponse.data.data) {
          this.renderSeverityByAssetTypeChart(severityByTypeResponse.data.data)
        }

        // Process trends
        if (trendsResponse.data && trendsResponse.data.trends) {
          this.renderAssetTrendsChart(trendsResponse.data.trends)
        }

        // Process provider distribution
        if (providerDistResponse.data && providerDistResponse.data.providers) {
          this.renderProviderDistributionChart(providerDistResponse.data.providers)
        }

        // Process assets table
        if (assetsResponse.data) {
          this.assets = assetsResponse.data.assets || []
          this.totalCount = assetsResponse.data.total_count || 0
          this.totalPages = assetsResponse.data.total_pages || 0
        }

        this.updated = new Date().toLocaleString()
      } catch (error) {
        console.error('Error loading data:', error)
      } finally {
        this.loading = false
        this.tableLoading = false
      }
    },

    renderAssetTypeChart(distribution) {
      this.assetTypeChartOptions = {
        ...this.assetTypeChartOptions,
        labels: distribution.map(d => this.capitalize(d._id || 'Unknown'))
      }
      this.assetTypeChartSeries = distribution.map(d => d.count)
    },

    renderTopVulnerableAssetsChart(assets) {
      this.topVulnerableAssetsChartOptions = {
        ...this.topVulnerableAssetsChartOptions,
        xaxis: {
          ...this.topVulnerableAssetsChartOptions.xaxis,
          categories: assets.map(asset => this.truncateText(asset._id, 25))
        },
        colors: assets.map(asset => {
          if (asset.critical_count > 0) return '#802020'
          if (asset.high_count > 0) return '#DC001E'
          return '#FECA00'
        })
      }
      this.topVulnerableAssetsChartSeries = [{
        name: 'Vulnerabilities',
        data: assets.map(asset => asset.vuln_count)
      }]
      this.topVulnerableAssets = assets
    },

    renderRiskDistributionChart(distribution) {
      const riskOrder = ['critical', 'high', 'medium', 'low']
      const colorMap = {
        critical: '#802020',
        high: '#DC001E',
        medium: '#FECA00',
        low: '#01AD00'
      }

      const sorted = riskOrder
        .map(risk => distribution.find(d => d._id === risk))
        .filter(d => d)

      this.riskDistributionChartOptions = {
        ...this.riskDistributionChartOptions,
        labels: sorted.map(d => this.capitalize(d._id)),
        colors: sorted.map(d => colorMap[d._id] || '#6c757d')
      }
      this.riskDistributionChartSeries = sorted.map(d => d.count)
    },

    renderSeverityByAssetTypeChart(data) {
      const assetTypes = [...new Set(data.map(d => d.asset_type))]
      const severities = ['critical', 'high', 'medium', 'low']

      this.severityByAssetTypeChartOptions = {
        ...this.severityByAssetTypeChartOptions,
        xaxis: {
          categories: assetTypes.map(t => this.capitalize(t))
        }
      }

      this.severityByAssetTypeChartSeries = severities.map(severity => ({
        name: this.capitalize(severity),
        data: assetTypes.map(type => {
          const item = data.find(d => d.asset_type === type && d.severity === severity)
          return item ? item.count : 0
        })
      }))
    },

    renderAssetTrendsChart(trends) {
      this.assetTrendsChartOptions = {
        ...this.assetTrendsChartOptions,
        xaxis: {
          ...this.assetTrendsChartOptions.xaxis,
          categories: trends.map(t => t._id)
        }
      }
      this.assetTrendsChartSeries = [
        { name: 'Total Assets', data: trends.map(t => t.asset_count) },
        { name: 'Critical Risk', data: trends.map(t => t.critical_count || 0) },
        { name: 'High Risk', data: trends.map(t => t.high_count || 0) }
      ]
    },

    renderProviderDistributionChart(providers) {
      this.providerDistributionChartOptions = {
        ...this.providerDistributionChartOptions,
        xaxis: {
          ...this.providerDistributionChartOptions.xaxis,
          categories: providers.map(p => p._id || 'Unknown')
        }
      }
      this.providerDistributionChartSeries = [{
        name: 'Assets',
        data: providers.map(p => p.count)
      }]
    },

    truncateText(text, maxLength) {
      if (!text) return ''
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
    },

    capitalize(str) {
      if (!str) return ''
      return str.charAt(0).toUpperCase() + str.slice(1)
    },

    getRiskClass(riskScore) {
      if (riskScore >= 9) return 'badge badge-severity-critical'
      if (riskScore >= 7) return 'badge badge-severity-high'
      if (riskScore >= 4) return 'badge badge-severity-medium'
      return 'badge badge-severity-low'
    },

    getRiskLevel(riskScore) {
      if (riskScore >= 9) return 'Critical'
      if (riskScore >= 7) return 'High'
      if (riskScore >= 4) return 'Medium'
      return 'Low'
    },

    getYesNoClass(value) {
      return value === 'Y' ? 'badge badge-yes' : 'badge badge-no'
    },

    formatDate(dateStr) {
      if (!dateStr) return 'N/A'
      try {
        const date = new Date(dateStr.$date || dateStr)
        return date.toLocaleDateString()
      } catch (e) {
        return 'N/A'
      }
    },

    toggleSort(key) {
      const column = this.columns.find(col => col.key === key)
      if (!column || !column.sortable) return

      if (this.sortKey === key) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortKey = key
        this.sortDirection = 'desc'
      }

      this.loadAllData()
    },

    getSortIcon(key) {
      if (this.sortKey !== key) return 'mdi mdi-sort'
      return this.sortDirection === 'asc' ? 'mdi mdi-sort-ascending' : 'mdi mdi-sort-descending'
    },

    goToPage(page) {
      if (page < 1 || page > this.totalPages) return
      this.currentPage = page
      this.loadAllData()
    },

    getSeverityCheckboxClass(severity) {
      const classes = {
        critical: 'severity-critical',
        high: 'severity-high',
        medium: 'severity-medium',
        low: 'severity-low'
      }
      const key = severity ? severity.toLowerCase() : ''
      return classes[key] || ''
    },

    async getChartDataURI(chartRef) {
      if (chartRef && chartRef.chart) {
        try {
          const result = await chartRef.chart.dataURI({ scale: 1.5 })
          return result.imgURI
        } catch (e) {
          console.error('Error exporting chart:', e)
          return null
        }
      }
      return null
    },

    async downloadPdf() {
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      })

      const pageWidth = 297
      const pageHeight = 210
      const margin = 12

      const colors = {
        primary: [85, 110, 230],
        danger: [220, 53, 69],
        warning: [255, 193, 7],
        info: [23, 162, 184],
        success: [40, 167, 69],
        secondary: [108, 117, 125],
        dark: [52, 58, 64],
        light: [248, 249, 250]
      }

      // Page 1
      doc.setFontSize(18)
      doc.setTextColor(...colors.dark)
      doc.text('Prisma Cloud Assets Report', margin, 15)

      doc.setFontSize(9)
      doc.setTextColor(...colors.secondary)
      doc.text(`Generated: ${new Date().toLocaleString()}`, margin, 22)

      // Stats Row
      const statsY = 30
      const statsHeight = 20
      const statsWidth = 43
      const statsGap = 2
      const statsData = [
        { label: 'Total Assets', value: this.stats.total_assets, color: colors.primary },
        { label: 'Critical Risk', value: this.stats.critical_assets, color: colors.danger },
        { label: 'High Risk', value: this.stats.high_risk_assets, color: colors.warning },
        { label: 'Internet Exposed', value: this.stats.internet_exposed_assets, color: colors.info },
        { label: 'Total Vulnerabilities', value: this.stats.total_vulnerabilities, color: colors.secondary },
        { label: 'Avg Vulns/Asset', value: (this.stats.avg_vulns_per_asset || 0).toFixed(1), color: colors.success }
      ]

      statsData.forEach((stat, index) => {
        const x = margin + (index * (statsWidth + statsGap))

        doc.setFillColor(248, 249, 250)
        doc.roundedRect(x, statsY, statsWidth, statsHeight, 2, 2, 'F')

        doc.setFillColor(...stat.color)
        doc.rect(x, statsY, 3, statsHeight, 'F')

        doc.setFontSize(7)
        doc.setTextColor(...colors.secondary)
        doc.text(stat.label, x + 5, statsY + 6)

        doc.setFontSize(13)
        doc.setTextColor(...colors.dark)
        doc.text(String(stat.value), x + 5, statsY + 15)
      })

      // Charts Row 1
      const chartsY = 65
      const donutChartSize = 85
      const availableWidth = pageWidth - 2 * margin
      const donutChartGap = (availableWidth - 3 * donutChartSize) / 2

      const assetTypeImg = await this.getChartDataURI(this.$refs.assetTypeChart)
      const riskDistImg = await this.getChartDataURI(this.$refs.riskDistributionChart)
      const providerDistImg = await this.getChartDataURI(this.$refs.providerDistributionChart)

      const chart1X = margin
      if (assetTypeImg) {
        doc.setFontSize(10)
        doc.setTextColor(...colors.dark)
        doc.text('Asset Type Distribution', chart1X, chartsY - 4)
        doc.addImage(assetTypeImg, 'JPEG', chart1X, chartsY, donutChartSize * 1.5, donutChartSize)
      }

      const chart2X = margin + donutChartSize + donutChartGap
      if (riskDistImg) {
        doc.setFontSize(10)
        doc.setTextColor(...colors.dark)
        doc.text('Risk Distribution', chart2X, chartsY - 4)
        doc.addImage(riskDistImg, 'JPEG', chart2X, chartsY, donutChartSize * 1.5, donutChartSize)
      }

      const chart3X = margin + 2 * (donutChartSize + donutChartGap)
      if (providerDistImg) {
        doc.setFontSize(10)
        doc.setTextColor(...colors.dark)
        doc.text('Provider Distribution', chart3X, chartsY - 4)
        doc.addImage(providerDistImg, 'JPEG', chart3X, chartsY, donutChartSize * 1.5, donutChartSize)
      }

      doc.setFontSize(8)
      doc.setTextColor(...colors.secondary)
      const filterText = `Filters: Date Range: Last ${this.selectedDateRange} days | Risk Level: ${this.selectedRiskLevel}`
      doc.text(filterText, margin, pageHeight - 6)

      // Page 2
      doc.addPage()

      const barChartWidth = (pageWidth - 2 * margin - 8) / 2
      const barChartHeight = 85
      const barChartY1 = 20
      const barChartY2 = barChartY1 + barChartHeight + 18
      const barChartGap = 8

      const topVulnerableImg = await this.getChartDataURI(this.$refs.topVulnerableAssetsChart)
      if (topVulnerableImg) {
        doc.setFontSize(11)
        doc.setTextColor(...colors.dark)
        doc.text('Top 10 Vulnerable Assets', margin, 15)
        doc.addImage(topVulnerableImg, 'JPEG', margin, barChartY1, barChartWidth * 1.5, barChartHeight * 1.5)
      }

      const severityByTypeImg = await this.getChartDataURI(this.$refs.severityByAssetTypeChart)
      if (severityByTypeImg) {
        doc.setFontSize(11)
        doc.setTextColor(...colors.dark)
        doc.text('Severity by Asset Type', margin + barChartWidth + barChartGap, 15)
        doc.addImage(severityByTypeImg, 'JPEG', margin + barChartWidth + barChartGap, barChartY1, barChartWidth * 1.5, barChartHeight * 1.5)
      }

      const trendsImg = await this.getChartDataURI(this.$refs.assetTrendsChart)
      if (trendsImg) {
        doc.setFontSize(11)
        doc.setTextColor(...colors.dark)
        doc.text('Asset Trends', margin, barChartY2 - 5)
        doc.addImage(trendsImg, 'JPEG', margin, barChartY2, barChartWidth * 1.5, barChartHeight * 1.5)
      }

      doc.save('Prisma Cloud Assets Report.pdf')
    },

    async exportToCsv() {
      this.exporting = true
      const baseURL = this.appConfig.xdrAPI.baseURL || 'http://localhost:5000'
      const queryString = this.buildQueryString(this.filterParams)

      try {
        // Fetch all assets (no pagination)
        const response = await axios.get(
          `${baseURL}/prismacloud/assets?${queryString}&page=1&page_size=${this.totalCount || 10000}&sort_by=${this.sortKey}&sort_dir=${this.sortDirection}`
        )

        if (!response.data || !response.data.assets) {
          console.error('No data to export')
          return
        }

        const assets = response.data.assets

        // Define CSV headers
        const headers = [
          'Asset Name',
          'Asset Type',
          'Service Provider',
          'Owner',
          'Total Vulnerabilities',
          'Critical',
          'High',
          'Medium',
          'Low',
          'Exploitable',
          'Internet Exposed',
          'Risk Score',
          'Risk Level',
          'Last Seen'
        ]

        // Convert data to CSV rows
        const rows = assets.map(asset => [
          asset.ASSET_NAME || '',
          asset.ASSET_TYPE || '',
          asset.SERVICE_PROVIDER || '',
          asset.OWNER || '',
          asset.VULNERABILITY_COUNT || 0,
          asset.CRITICAL_COUNT || 0,
          asset.HIGH_COUNT || 0,
          asset.MEDIUM_COUNT || 0,
          asset.LOW_COUNT || 0,
          asset.EXPLOITABLE_COUNT || 0,
          asset.INTERNET_EXPOSED || 'N',
          (asset.RISK_SCORE || 0).toFixed(1),
          this.getRiskLevel(asset.RISK_SCORE || 0),
          this.formatDate(asset.LAST_SEEN) || ''
        ])

        // Build CSV content
        const csvContent = [
          headers.join(','),
          ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
        ].join('\n')

        // Create and download file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', `prismacloud_assets_${new Date().toISOString().split('T')[0]}.csv`)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (error) {
        console.error('Error exporting CSV:', error)
      } finally {
        this.exporting = false
      }
    }
  }
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="container" style="max-width: 1600px !important;">
      <!-- Download Buttons -->
      <div class="mb-3">
        <b-button
          class="btn btn-primary btn-sm"
          variant="light"
          style="margin-right: 12px;"
          @click="downloadPdf"
        >
          <feather type="download" class="icon-xs mr-1"></feather>
          Download PDF
        </b-button>
        <b-button
          class="btn btn-primary btn-sm"
          variant="light"
          :disabled="exporting || totalCount === 0"
          @click="exportToCsv"
        >
          <feather type="file-text" class="icon-xs mr-1"></feather>
          <span v-if="exporting">Exporting...</span>
          <span v-else>Export CSV</span>
        </b-button>
      </div>

      <!-- Filters Panel -->
      <div class="card mb-4 filter-panel">
        <div class="card-body py-3">
          <!-- Row 1: Main filters -->
          <div class="row g-3 align-items-end">
            <!-- Search -->
            <div class="col-lg-2 col-md-4">
              <label class="filter-label">Search</label>
              <input
                type="text"
                class="form-control form-control-sm"
                v-model="searchQuery"
                placeholder="Asset name..."
              >
            </div>

            <!-- Date Range -->
            <div class="col-lg-2 col-md-4">
              <label class="filter-label">Date Range</label>
              <select class="form-select form-select-sm" v-model="selectedDateRange">
                <option v-for="opt in dateRangeOptions" :key="opt.value" :value="opt.value">
                  {{ opt.text }}
                </option>
              </select>
            </div>

            <!-- Asset Type -->
            <div class="col-lg-2 col-md-4">
              <label class="filter-label">Asset Type</label>
              <select class="form-select form-select-sm" v-model="selectedAssetType">
                <option v-for="opt in assetTypeOptions" :key="opt.value" :value="opt.value">
                  {{ opt.text }}
                </option>
              </select>
            </div>

            <!-- Service Provider -->
            <div class="col-lg-2 col-md-4">
              <label class="filter-label">Service Provider</label>
              <select class="form-select form-select-sm" v-model="selectedServiceProvider">
                <option v-for="opt in serviceProviders" :key="opt.value" :value="opt.value">
                  {{ opt.text }}
                </option>
              </select>
            </div>

            <!-- Risk Level -->
            <div class="col-lg-2 col-md-4">
              <label class="filter-label">Risk Level</label>
              <select class="form-select form-select-sm" v-model="selectedRiskLevel">
                <option v-for="opt in riskLevelOptions" :key="opt.value" :value="opt.value">
                  {{ opt.text }}
                </option>
              </select>
            </div>

            <!-- Internet Exposed -->
            <div class="col-lg-2 col-md-4">
              <label class="filter-label">Internet Exposed</label>
              <select class="form-select form-select-sm" v-model="selectedInternetExposed">
                <option v-for="opt in internetExposedOptions" :key="opt.value" :value="opt.value">
                  {{ opt.text }}
                </option>
              </select>
            </div>
          </div>

          <!-- Row 2: Severity filters and updated timestamp -->
          <div class="row g-3 align-items-end mt-1">
            <!-- Severity Checkboxes -->
            <div class="col-lg-8 col-md-8">
              <label class="filter-label">Severity Filter (for vulnerability counts)</label>
              <div class="severity-filters">
                <label
                  v-for="opt in severityOptions"
                  :key="opt.value"
                  class="severity-checkbox"
                  :class="[getSeverityCheckboxClass(opt.value), { 'is-checked': selectedSeverities.includes(opt.value) }]"
                >
                  <input
                    type="checkbox"
                    :value="opt.value"
                    v-model="selectedSeverities"
                  >
                  {{ opt.text }}
                </label>
              </div>
            </div>

            <!-- Updated timestamp -->
            <div class="col-lg-4 col-md-4 text-end">
              <span class="updated-text">Updated: {{ updated }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border" role="status">
          <span class="sr-only">Loading...</span>
        </div>
        <p class="mt-2">Loading asset data...</p>
      </div>

      <div v-else>
        <!-- Stats Cards -->
        <div class="row mb-4">
          <div class="col-md-2">
            <div class="card mini-stats-wid">
              <div class="card-body">
                <div class="d-flex">
                  <div class="flex-grow-1">
                    <p class="text-muted fw-medium mb-2">Total Assets</p>
                    <h4 class="mb-0">{{ stats.total_assets.toLocaleString() }}</h4>
                  </div>
                  <div class="mini-stat-icon avatar-sm align-self-center rounded-circle bg-primary">
                    <span class="avatar-title">
                      <feather type="server" class="icon-sm"></feather>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-2">
            <div class="card mini-stats-wid">
              <div class="card-body">
                <div class="d-flex">
                  <div class="flex-grow-1">
                    <p class="text-muted fw-medium mb-2">Critical Risk</p>
                    <h4 class="mb-0 text-danger">{{ stats.critical_assets.toLocaleString() }}</h4>
                  </div>
                  <div class="mini-stat-icon avatar-sm align-self-center rounded-circle bg-danger">
                    <span class="avatar-title">
                      <feather type="alert-triangle" class="icon-sm"></feather>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-2">
            <div class="card mini-stats-wid">
              <div class="card-body">
                <div class="d-flex">
                  <div class="flex-grow-1">
                    <p class="text-muted fw-medium mb-2">High Risk</p>
                    <h4 class="mb-0 text-warning">{{ stats.high_risk_assets.toLocaleString() }}</h4>
                  </div>
                  <div class="mini-stat-icon avatar-sm align-self-center rounded-circle bg-warning">
                    <span class="avatar-title">
                      <feather type="alert-octagon" class="icon-sm"></feather>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-2">
            <div class="card mini-stats-wid">
              <div class="card-body">
                <div class="d-flex">
                  <div class="flex-grow-1">
                    <p class="text-muted fw-medium mb-2">Internet Exposed</p>
                    <h4 class="mb-0 text-info">{{ stats.internet_exposed_assets.toLocaleString() }}</h4>
                  </div>
                  <div class="mini-stat-icon avatar-sm align-self-center rounded-circle bg-info">
                    <span class="avatar-title">
                      <feather type="globe" class="icon-sm"></feather>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-2">
            <div class="card mini-stats-wid">
              <div class="card-body">
                <div class="d-flex">
                  <div class="flex-grow-1">
                    <p class="text-muted fw-medium mb-2">Total Vulnerabilities</p>
                    <h4 class="mb-0">{{ stats.total_vulnerabilities.toLocaleString() }}</h4>
                  </div>
                  <div class="mini-stat-icon avatar-sm align-self-center rounded-circle bg-secondary">
                    <span class="avatar-title">
                      <feather type="shield" class="icon-sm"></feather>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-2">
            <div class="card mini-stats-wid">
              <div class="card-body">
                <div class="d-flex">
                  <div class="flex-grow-1">
                    <p class="text-muted fw-medium mb-2">Avg Vulns/Asset</p>
                    <h4 class="mb-0">{{ (stats.avg_vulns_per_asset || 0).toFixed(1) }}</h4>
                  </div>
                  <div class="mini-stat-icon avatar-sm align-self-center rounded-circle bg-success">
                    <span class="avatar-title">
                      <feather type="trending-up" class="icon-sm"></feather>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Charts Row 1 -->
        <div class="row mb-4">
          <div class="col-md-4">
            <div class="card">
              <div class="card-body">
                <h5 class="card-title">Asset Type Distribution</h5>
                <apexchart
                  ref="assetTypeChart"
                  v-if="assetTypeChartSeries.length > 0"
                  height="280"
                  :options="assetTypeChartOptions"
                  :series="assetTypeChartSeries"
                ></apexchart>
                <div v-else class="text-center py-5 text-muted">No data available</div>
              </div>
            </div>
          </div>

          <div class="col-md-4">
            <div class="card">
              <div class="card-body">
                <h5 class="card-title">Risk Distribution</h5>
                <apexchart
                  ref="riskDistributionChart"
                  v-if="riskDistributionChartSeries.length > 0"
                  height="280"
                  :options="riskDistributionChartOptions"
                  :series="riskDistributionChartSeries"
                ></apexchart>
                <div v-else class="text-center py-5 text-muted">No data available</div>
              </div>
            </div>
          </div>

          <div class="col-md-4">
            <div class="card">
              <div class="card-body">
                <h5 class="card-title">Provider Distribution</h5>
                <apexchart
                  ref="providerDistributionChart"
                  v-if="providerDistributionChartSeries.length > 0"
                  height="280"
                  :options="providerDistributionChartOptions"
                  :series="providerDistributionChartSeries"
                ></apexchart>
                <div v-else class="text-center py-5 text-muted">No data available</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Charts Row 2 -->
        <div class="row mb-4">
          <div class="col-md-6">
            <div class="card">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <h5 class="card-title mb-0">Top 10 Vulnerable Assets</h5>
                  <div class="severity-legend">
                    <span class="legend-item"><span class="legend-color bg-severity-critical"></span>Critical</span>
                    <span class="legend-item"><span class="legend-color bg-severity-high"></span>High</span>
                    <span class="legend-item"><span class="legend-color bg-severity-medium"></span>Medium</span>
                  </div>
                </div>
                <apexchart
                  ref="topVulnerableAssetsChart"
                  v-if="topVulnerableAssetsChartSeries.length > 0"
                  height="320"
                  :options="topVulnerableAssetsChartOptions"
                  :series="topVulnerableAssetsChartSeries"
                ></apexchart>
                <div v-else class="text-center py-5 text-muted">No data available</div>
              </div>
            </div>
          </div>

          <div class="col-md-6">
            <div class="card">
              <div class="card-body">
                <h5 class="card-title">Severity by Asset Type</h5>
                <apexchart
                  ref="severityByAssetTypeChart"
                  v-if="severityByAssetTypeChartSeries.length > 0"
                  height="320"
                  :options="severityByAssetTypeChartOptions"
                  :series="severityByAssetTypeChartSeries"
                ></apexchart>
                <div v-else class="text-center py-5 text-muted">No data available</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Charts Row 3 -->
        <div class="row mb-4">
          <div class="col-md-12">
            <div class="card">
              <div class="card-body">
                <h5 class="card-title">Asset Trends</h5>
                <apexchart
                  ref="assetTrendsChart"
                  v-if="assetTrendsChartSeries.length > 0"
                  height="320"
                  :options="assetTrendsChartOptions"
                  :series="assetTrendsChartSeries"
                ></apexchart>
                <div v-else class="text-center py-5 text-muted">No data available</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Assets Table -->
      <div class="card position-relative">
        <!-- Table Loading Overlay -->
        <div v-if="tableLoading" class="table-loading-overlay">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Loading...</span>
          </div>
          <p class="mt-2 mb-0 text-muted">Loading assets...</p>
        </div>
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5 class="card-title mb-0">Assets ({{ totalCount.toLocaleString() }} total)</h5>
            <div>
              <span class="text-muted me-3">
                Page {{ currentPage }} of {{ totalPages }}
              </span>
              <button
                class="btn btn-sm btn-outline-secondary me-1"
                :disabled="currentPage === 1"
                @click="goToPage(currentPage - 1)"
              >
                Previous
              </button>
              <button
                class="btn btn-sm btn-outline-secondary"
                :disabled="currentPage >= totalPages"
                @click="goToPage(currentPage + 1)"
              >
                Next
              </button>
            </div>
          </div>

          <div class="table-responsive">
            <table class="table table-striped table-hover">
              <thead>
                <tr>
                  <th
                    v-for="col in columns"
                    :key="col.key"
                    :class="{ 'sortable': col.sortable, 'active': sortKey === col.key }"
                    @click="col.sortable && toggleSort(col.key)"
                    style="cursor: pointer;"
                  >
                    {{ col.label }}
                    <i v-if="col.sortable" :class="getSortIcon(col.key)" class="ms-1"></i>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(asset, index) in assets" :key="index">
                  <td class="col-asset-name">
                    <span v-b-tooltip.hover.top :title="asset.ASSET_NAME || ''">{{ truncateText(asset.ASSET_NAME, 40) }}</span>
                  </td>
                  <td>{{ asset.ASSET_TYPE }}</td>
                  <td class="col-provider">
                    <span v-b-tooltip.hover.top :title="asset.SERVICE_PROVIDER || 'N/A'">{{ truncateText(asset.SERVICE_PROVIDER, 15) || 'N/A' }}</span>
                  </td>
                  <td class="col-owner">
                    <span v-b-tooltip.hover.top :title="asset.OWNER || 'N/A'">{{ truncateText(asset.OWNER, 15) || 'N/A' }}</span>
                  </td>
                  <td><span class="badge badge-vuln-total">{{ asset.VULNERABILITY_COUNT || 0 }}</span></td>
                  <td><span class="badge badge-severity-critical">{{ asset.CRITICAL_COUNT || 0 }}</span></td>
                  <td><span class="badge badge-severity-high">{{ asset.HIGH_COUNT || 0 }}</span></td>
                  <td><span class="badge badge-exploitable">{{ asset.EXPLOITABLE_COUNT || 0 }}</span></td>
                  <td><span :class="getYesNoClass(asset.INTERNET_EXPOSED)">{{ asset.INTERNET_EXPOSED || 'N' }}</span></td>
                  <td>
                    <span :class="getRiskClass(asset.RISK_SCORE || 0)">
                      {{ (asset.RISK_SCORE || 0).toFixed(1) }} - {{ getRiskLevel(asset.RISK_SCORE || 0) }}
                    </span>
                  </td>
                  <td>{{ formatDate(asset.LAST_SEEN) }}</td>
                </tr>
                <tr v-if="assets.length === 0">
                  <td :colspan="columns.length" class="text-center py-4">
                    No assets found matching your criteria.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div class="d-flex justify-content-center mt-3" v-if="totalPages > 1">
            <nav>
              <ul class="pagination pagination-sm mb-0">
                <li class="page-item" :class="{ disabled: currentPage === 1 }">
                  <a class="page-link" href="#" @click.prevent="goToPage(1)">First</a>
                </li>
                <li class="page-item" :class="{ disabled: currentPage === 1 }">
                  <a class="page-link" href="#" @click.prevent="goToPage(currentPage - 1)">Prev</a>
                </li>
                <li
                  v-for="page in Math.min(5, totalPages)"
                  :key="page"
                  class="page-item"
                  :class="{ active: currentPage === page }"
                >
                  <a class="page-link" href="#" @click.prevent="goToPage(page)">{{ page }}</a>
                </li>
                <li class="page-item" :class="{ disabled: currentPage >= totalPages }">
                  <a class="page-link" href="#" @click.prevent="goToPage(currentPage + 1)">Next</a>
                </li>
                <li class="page-item" :class="{ disabled: currentPage >= totalPages }">
                  <a class="page-link" href="#" @click.prevent="goToPage(totalPages)">Last</a>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
/* Button icon styling */
.icon-xs {
  width: 14px;
  height: 14px;
  vertical-align: middle;
}

.mr-1 {
  margin-right: 0.25rem;
}

/* Filter Panel Styles */
.filter-panel {
  background: #fff;
  border: 1px solid #e9ecef;
}

.filter-panel .card-body {
  padding: 1rem 1.25rem;
}

.filter-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.35rem;
}

.filter-panel .form-control,
.filter-panel .form-select {
  height: 34px;
  font-size: 0.85rem;
  border-color: #dee2e6;
}

.filter-panel .form-control:focus,
.filter-panel .form-select:focus {
  border-color: #556ee6;
  box-shadow: 0 0 0 0.15rem rgba(85, 110, 230, 0.15);
}

/* Severity Checkboxes */
.severity-filters {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  padding-top: 2px;
}

.severity-checkbox {
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.75rem;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  border: 1px solid transparent;
  background: #f8f9fa;
}

.severity-checkbox input[type="checkbox"] {
  display: none;
}

.severity-checkbox.is-checked {
  border-color: currentColor;
  background: rgba(0, 0, 0, 0.05);
}

/* Severity Colors */
.severity-critical {
  color: #802020;
}
.severity-critical:hover {
  background: rgba(128, 32, 32, 0.1);
}
.severity-critical.is-checked {
  background: rgba(128, 32, 32, 0.15);
  border-color: #802020;
}

.severity-high {
  color: #DC001E;
}
.severity-high:hover {
  background: rgba(220, 0, 30, 0.1);
}
.severity-high.is-checked {
  background: rgba(220, 0, 30, 0.15);
  border-color: #DC001E;
}

.severity-medium {
  color: #FECA00;
}
.severity-medium:hover {
  background: rgba(254, 202, 0, 0.15);
}
.severity-medium.is-checked {
  background: rgba(254, 202, 0, 0.2);
  border-color: #FECA00;
}

.severity-low {
  color: #01AD00;
}
.severity-low:hover {
  background: rgba(1, 173, 0, 0.1);
}
.severity-low.is-checked {
  background: rgba(1, 173, 0, 0.15);
  border-color: #01AD00;
}

/* Updated text */
.updated-text {
  font-size: 0.8rem;
  color: #6c757d;
  display: inline-flex;
  align-items: center;
  padding-top: 1.5rem;
}

/* Stats Cards */
.mini-stats-wid .card-body {
  padding: 1rem;
}

.mini-stat-icon {
  width: 48px;
  height: 48px;
}

.avatar-title {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #fff;
}

.avatar-title .icon-sm {
  width: 20px;
  height: 20px;
}

/* Table Styles */
.sortable {
  cursor: pointer;
}

.sortable:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.sortable.active {
  background-color: rgba(0, 0, 0, 0.075);
}

.table th {
  font-size: 0.85rem;
  white-space: nowrap;
}

.table td {
  font-size: 0.85rem;
  vertical-align: middle;
}

.badge {
  font-size: 0.75rem;
}

.card-title {
  font-size: 1rem;
  font-weight: 600;
}

/* Severity Legend */
.severity-legend {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  font-size: 0.75rem;
  color: #6c757d;
}

.legend-color {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 2px;
  margin-right: 4px;
}

.bg-severity-critical {
  background-color: #802020;
}

.bg-severity-high {
  background-color: #DC001E;
}

.bg-severity-medium {
  background-color: #FECA00;
}

.bg-severity-low {
  background-color: #01AD00;
}

/* Badge severity colors for table */
.badge-severity-critical {
  background-color: #802020;
  color: #fff;
}

.badge-severity-high {
  background-color: #DC001E;
  color: #fff;
}

.badge-severity-medium {
  background-color: #FECA00;
  color: #212529;
}

.badge-severity-low {
  background-color: #01AD00;
  color: #fff;
}

/* Vulnerability total badge */
.badge-vuln-total {
  background-color: #5470C6;
  color: #fff;
}

/* Exploitable badge */
.badge-exploitable {
  background-color: #9A60B4;
  color: #fff;
}

/* Yes/No badge colors */
.badge-yes {
  background-color: #E57373;
  color: #fff;
}

.badge-no {
  background-color: #81C784;
  color: #fff;
}

/* Table Loading Overlay */
.table-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.85);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 0.25rem;
}

.table-loading-overlay .spinner-border {
  width: 2.5rem;
  height: 2.5rem;
}

/* Tooltip columns */
.col-asset-name span,
.col-provider span,
.col-owner span {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: help;
}

.col-asset-name {
  max-width: 300px;
}

.col-provider {
  max-width: 120px;
}

.col-owner {
  max-width: 120px;
}

/* Responsive adjustments */
@media (max-width: 991px) {
  .severity-filters {
    margin-top: 0.5rem;
  }

  .updated-text {
    padding-top: 0.5rem;
    justify-content: flex-start;
  }
}
</style>
